/**
 * The light value of every text / icon / border / surface design token that the dark colour mode (`:root[data-color-mode='dark']` in
 * `src/styles/ds/tokens.css`) overrides. The document sheet is white paper in every theme, so the editing chrome drawn on it (placeholders,
 * "Add element", labels, selection outlines, icon buttons) must read these light values; otherwise a dark host theme hands it near-white
 * text on a white sheet. `DocumentSheetTokens` pins them on the sheet, and `documentSheetLightTokens.test.ts` keeps this list equal to what
 * `tokens.css` declares, so a token added to the dark block cannot silently escape.
 */
export const DOCUMENT_SHEET_LIGHT_TOKENS: Readonly<Record<string, string>> = {
  '--border-critical': '#840000',
  '--border-critical-hover': '#580000',
  '--border-critical-subtle': '#F19999',
  '--border-disabled': '#BABDC7',
  '--border-focus': '#183586',
  '--border-information': '#004177',
  '--border-information-subtle': '#99C5E8',
  '--border-neutral': '#BABDC7',
  '--border-neutral-active': '#757C90',
  '--border-neutral-bold': '#313747',
  '--border-neutral-bold-active': '#525B74',
  '--border-neutral-bold-hover': '#42495D',
  '--border-neutral-hover': '#979DAB',
  '--border-neutral-subtle': '#DCDEE3',
  '--border-neutral-subtle-hover': '#BABDC7',
  '--border-primary': '#183586',
  '--border-primary-hover': '#122865',
  '--border-primary-subtle': '#A5B4DC',
  '--border-success': '#005C1C',
  '--border-success-subtle': '#99D6AB',
  '--border-warning': '#8A6500',
  '--border-warning-subtle': '#F5DC99',
  '--icon-critical': '#840000',
  '--icon-critical-hover': '#580000',
  '--icon-disabled': '#757C90',
  '--icon-information': '#004177',
  '--icon-information-hover': '#002C4F',
  '--icon-information-on-color': '#FDFDFD',
  '--icon-neutral': '#101217',
  '--icon-neutral-hover': '#313747',
  '--icon-neutral-interactive': '#525B74',
  '--icon-neutral-interactive-hover': '#1F232F',
  '--icon-neutral-on-color': '#FDFDFD',
  '--icon-neutral-on-color-hover': '#F6F7F8',
  '--icon-primary': '#152F76',
  '--icon-primary-hover': '#1E43A8',
  '--icon-primary-subtle': '#1E43A8',
  '--icon-success': '#005C1C',
  '--icon-success-hover': '#003D12',
  '--icon-success-on-color': '#FDFDFD',
  '--icon-warning': '#8A6500',
  '--icon-warning-hover': '#5C4300',
  '--icon-warning-on-color': '#08090C',
  '--surface-accent-lime': '#E2EDCC',
  '--surface-accent-lime-bold': '#6DA300',
  '--surface-accent-lime-bold-hover': '#578200',
  '--surface-accent-lime-hover': '#C5DA99',
  '--surface-accent-magenta': '#F7D2E2',
  '--surface-accent-magenta-bold': '#D71F6F',
  '--surface-accent-magenta-bold-hover': '#AC1959',
  '--surface-accent-magenta-hover': '#EFA5C5',
  '--surface-accent-orange': '#FCE0CC',
  '--surface-accent-orange-bold': '#F06400',
  '--surface-accent-orange-bold-hover': '#C05000',
  '--surface-accent-orange-hover': '#F9C199',
  '--surface-accent-purple': '#E9D7F2',
  '--surface-accent-purple-bold': '#9137BE',
  '--surface-accent-purple-bold-hover': '#742C98',
  '--surface-accent-purple-hover': '#D3AFE5',
  '--surface-accent-teal': '#CCE9EE',
  '--surface-accent-teal-bold': '#0092A8',
  '--surface-accent-teal-bold-hover': '#007587',
  '--surface-accent-teal-hover': '#99D3DC',
  '--surface-chrome-avatar': '#FDFDFD',
  '--surface-critical': '#F8CCCC',
  '--surface-critical-bold': '#DB0000',
  '--surface-critical-bold-active': '#840000',
  '--surface-critical-bold-hover': '#AF0000',
  '--surface-critical-hover': '#F19999',
  '--surface-disabled': '#F6F7F8',
  '--surface-information': '#CCE2F4',
  '--surface-information-bold': '#00579E',
  '--surface-information-bold-hover': '#004177',
  '--surface-information-hover': '#99C5E8',
  '--surface-neutral-active': '#BABDC7',
  '--surface-neutral-bold': '#313747',
  '--surface-neutral-bold-active': '#525B74',
  '--surface-neutral-bold-hover': '#42495D',
  '--surface-neutral-container-primary': '#FDFDFD',
  '--surface-neutral-container-secondary': '#F6F7F8',
  '--surface-neutral-container-tertiary': '#EDEEF1',
  '--surface-neutral-hover': '#DCDEE3',
  '--surface-neutral-page': '#EDEEF1',
  '--surface-neutral-subtle-hover': '#EDEEF1',
  '--surface-neutral-subtle-selected': '#DCDEE3',
  '--surface-neutral-subtle-selected-hover': '#BABDC7',
  '--surface-primary': '#152F76',
  '--surface-primary-active': '#4464C0',
  '--surface-primary-hover': '#1E43A8',
  '--surface-primary-subtle': '#E8ECF6',
  '--surface-primary-subtle-hover': '#D2D9EE',
  '--surface-sidebar': '#EDEEF1',
  '--surface-sidebar-hover': '#F6F7F8',
  '--surface-sidebar-selected': '#FDFDFD',
  '--surface-success': '#CCEBD5',
  '--surface-success-bold': '#00992E',
  '--surface-success-bold-hover': '#007A25',
  '--surface-success-hover': '#99D6AB',
  '--surface-warning': '#FAEECC',
  '--surface-warning-bold': '#E6A800',
  '--surface-warning-bold-hover': '#B88600',
  '--surface-warning-hover': '#F5DC99',
  '--text-accent-lime': '#416200',
  '--text-accent-lime-hover': '#2C4100',
  '--text-accent-magenta': '#811243',
  '--text-accent-magenta-hover': '#560C2C',
  '--text-accent-orange': '#903C00',
  '--text-accent-orange-hover': '#602800',
  '--text-accent-purple': '#572172',
  '--text-accent-purple-hover': '#3A164C',
  '--text-accent-teal': '#005865',
  '--text-accent-teal-hover': '#003A43',
  '--text-body': '#101217',
  '--text-caption': '#101217',
  '--text-critical': '#840000',
  '--text-critical-hover': '#580000',
  '--text-critical-on-color': '#FDFDFD',
  '--text-disabled': '#757C90',
  '--text-heading': '#08090C',
  '--text-hero': '#08090C',
  '--text-information': '#004177',
  '--text-information-hover': '#002C4F',
  '--text-information-on-color': '#FDFDFD',
  '--text-interactive': '#525B74',
  '--text-on-color-body': '#FDFDFD',
  '--text-placeholder': '#525B74',
  '--text-primary': '#183586',
  '--text-primary-hover': '#122865',
  '--text-success': '#005C1C',
  '--text-success-hover': '#003D12',
  '--text-success-on-color': '#FDFDFD',
  '--text-warning': '#8A6500',
  '--text-warning-hover': '#5C4300',
  '--text-warning-on-color': '#08090C'
};

/** The rule that pins 134 tokens to their light values on every document sheet. */
export function documentSheetTokensCss(): string {
  const declarations = Object.entries(DOCUMENT_SHEET_LIGHT_TOKENS)
    .map(([name, value]) => `${name}:${value}`)
    .join(';');
  return `[data-document-sheet]{${declarations}}`;
}
